import { InputLabel, Input, InputAdornment, IconButton } from "@mui/material";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import { ErrorMessage } from "formik";
import React from "react";
import styled from "styled-components";

import { StyledFormControl, StyledError } from "@components";
import { IHandleChange, IHandleBlur } from "@interfaces";

interface InputComponentProp {
    handleChange: IHandleChange;
    handleBlur: IHandleBlur;
    value: string | Date | null;
    name: string;
    label: string;
    type: string;
    focus?: boolean;
    defaultValue?: string | Date | null | undefined;
    required?: boolean;
    userId?: string | number;
    placeHolder?: string;
    img?: string;
    error?: string;
    className?: string;
}

// Email validation helper - only allows alphanumeric, dots, underscores, plus, and hyphens
export const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
};

export const InputCommon = (props: InputComponentProp) => {
    const [showPassword, setShowPassword] = React.useState<boolean>(false);
    const [emailError, setEmailError] = React.useState<string>("");
    const {
        name,
        label,
        value,
        type,
        focus,
        handleChange,
        handleBlur,
        defaultValue,
        placeHolder,
        userId,
        img,
        error,
        className,
    } = props;

    const handleBlurWithValidation =
        (fieldName: string) => (e: React.FocusEvent<HTMLInputElement>) => {
            // Validate email on blur
            if (type === "email" && value) {
                const emailValue = String(value);
                if (!isValidEmail(emailValue)) {
                    setEmailError("Please enter a valid email address");
                } else {
                    setEmailError("");
                }
            }
            // Call the original handleBlur
            handleBlur(fieldName)(e);
        };

    // Clear email error when value changes and becomes valid
    React.useEffect(() => {
        if (type === "email" && value && emailError) {
            if (isValidEmail(String(value))) {
                setEmailError("");
            }
        }
    }, [value, type, emailError]);

    return (
        <>
            <StyledFormControl variant="standard">
                <StyledLabel htmlFor={"" + name + userId}>{label}</StyledLabel>
                <Input
                    className={className}
                    onChange={handleChange(name)}
                    value={value || ""}
                    fullWidth
                    id={"" + name + userId}
                    name={name}
                    onBlur={handleBlurWithValidation(name)}
                    type={showPassword ? "text" : type}
                    autoComplete={name}
                    autoFocus={focus}
                    defaultValue={defaultValue}
                    placeholder={placeHolder}
                    style={{
                        color: value ? "#090A18" : "transparent",
                    }}
                    sx={placeHolder && !value ? {
                        "& input::placeholder": {
                            color: "#999",
                            opacity: 1,
                        },
                    } : undefined}
                    endAdornment={
                        type === "password" ? (
                            <InputAdornment position="start">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="start"
                                >
                                    {showPassword ? (
                                        <VisibilityOutlined />
                                    ) : (
                                        <VisibilityOffOutlined />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ) : img ? (
                            <InputAdornment position="end">
                                <EmailIcon />
                            </InputAdornment>
                        ) : null
                    }
                />
            </StyledFormControl>
            {emailError ? (
                <StyledError>{emailError}</StyledError>
            ) : error ? (
                <StyledError>{error}</StyledError>
            ) : (
                <ErrorMessage name={name} component={StyledError} />
            )}{" "}
        </>
    );
};

const StyledLabel = styled(InputLabel)`
    span {
        color: ${(p) => p.theme.colors.red};
    }
`;
